// Hook para eliminar paciente. Wrap de RPC eliminar_paciente_sin_actividad
// (migration 0009). DELETE conservador con bloqueos por actividad.

import { useState } from "react";
import { supabase } from "../client";

export type EliminarPacienteError =
  | { code: "ALREADY_ACTIVE"; message: string }
  | { code: "HAS_SESSIONS"; message: string; count: number }
  | { code: "HAS_TRATAMIENTOS"; message: string; count: number }
  | { code: "NOT_FOUND"; message: string }
  | { code: "NO_AUTH"; message: string }
  | { code: "OTHER"; message: string };

export interface UseEliminarPacienteReturn {
  eliminar: (pacienteId: string) => Promise<boolean>;
  loading: boolean;
  error: EliminarPacienteError | null;
  reset: () => void;
}

export function useEliminarPaciente(): UseEliminarPacienteReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<EliminarPacienteError | null>(null);

  async function eliminar(pacienteId: string): Promise<boolean> {
    setLoading(true);
    setError(null);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "eliminar_paciente_sin_actividad",
        { p_paciente_id: pacienteId },
      );

      if (rpcError) {
        console.warn("[useEliminarPaciente] rpc error", rpcError);
        setError({ code: "OTHER", message: rpcError.message });
        return false;
      }

      if (rpcData && !rpcData.ok) {
        const code = rpcData.code as string;
        if (code === "ALREADY_ACTIVE") {
          setError({
            code: "ALREADY_ACTIVE",
            message:
              "El paciente ya activó su cuenta. No se puede eliminar — archivado queda para una próxima versión.",
          });
        } else if (code === "HAS_SESSIONS") {
          setError({
            code: "HAS_SESSIONS",
            message: `El paciente tiene ${rpcData.count} sesión(es) registradas. No se puede eliminar — archivado queda para una próxima versión.`,
            count: rpcData.count as number,
          });
        } else if (code === "HAS_TRATAMIENTOS") {
          setError({
            code: "HAS_TRATAMIENTOS",
            message: `El paciente tiene ${rpcData.count} tratamiento(s) registrado(s). No se puede eliminar — archivado queda para una próxima versión.`,
            count: rpcData.count as number,
          });
        } else if (code === "NOT_FOUND") {
          setError({
            code: "NOT_FOUND",
            message: "No encontramos el paciente.",
          });
        } else if (code === "NO_AUTH") {
          setError({
            code: "NO_AUTH",
            message: "Tu sesión expiró. Iniciá sesión de nuevo.",
          });
        } else {
          setError({
            code: "OTHER",
            message:
              (rpcData.message as string | undefined) ??
              "No pudimos eliminar el paciente.",
          });
        }
        return false;
      }

      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useEliminarPaciente] failed", e);
      setError({ code: "OTHER", message: msg });
      return false;
    } finally {
      setLoading(false);
    }
  }

  return { eliminar, loading, error, reset: () => setError(null) };
}
